import { useMemo } from 'react';
import { useLocation, Outlet } from 'react-router';
import {
    LayoutGrid01,
    Atom02,
    FileCheck02,
    HelpCircle,
    UserPlus01,
    BookOpen01,
    Bell01,
    Package
} from '@untitledui/icons';
import { SidebarNavigationSimple } from '@/components/application/app-navigation/sidebar-navigation/sidebar-simple';
import { AICTACard } from '@/components/application/ai-chat/ai-cta-card';
import type { NavItemType } from '@/components/application/app-navigation/config';
import { useTranslation } from 'react-i18next';
import { useMenuConfiguration } from '@/hooks/useMenuConfiguration';
import { MenuRouteGuard } from '@/components/application/route-guard/menu-route-guard';
import { useActivityUnreadCount } from '@/hooks/api/use-activities';

// Estendi NavItemType per includere key
type NavItemWithKey = NavItemType & { key?: string };

export const FreightForwarderLayout = () => {
    const location = useLocation();
    const currentPath = location.pathname;
    const { t } = useTranslation('navigation');
    const { isMenuItemEnabled } = useMenuConfiguration();

    // Fetch unread count for activity badge
    const { data: unreadCount = 0 } = useActivityUnreadCount('freight-forwarder');

    // Navigation items for sidebar (con chiavi per il filtraggio)
    const allSidebarItems: NavItemWithKey[] = useMemo(() => [
        {
            key: 'ask-eve',
            label: t('askEve'),
            href: '/freight-forwarder/ask-eve',
            icon: Atom02
        },
        {
            key: 'dashboard',
            label: t('controlHub'),
            href: '/freight-forwarder/control-hub',
            icon: LayoutGrid01
        },
        {
            key: 'quotes',
            label: t('quotes'),
            href: '/freight-forwarder/quotes',
            icon: FileCheck02
        },
        {
            key: 'containers',
            label: t('containers'),
            href: '/freight-forwarder/containers',
            icon: Package
        },
        {
            key: 'activity',
            label: t('activity'),
            href: '/freight-forwarder/activity',
            icon: Bell01,
            badge: unreadCount > 0 ? (
                <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2 items-center justify-center rounded-full bg-error-600 ring-1 ring-primary">
                    <span className="sr-only">{t('unreadNotifications', { count: unreadCount })}</span>
                </span>
            ) : undefined
        },
        {
            key: 'team',
            label: t('team'),
            href: '/freight-forwarder/team',
            icon: UserPlus01
        },
        {
            key: 'addressBook',
            label: t('addressBook'),
            href: '/freight-forwarder/address-book',
            icon: BookOpen01
        }
    ], [t, unreadCount]);

    // Filtra i sidebar items in base alla configurazione menu
    const sidebarItems = useMemo(() =>
        allSidebarItems.filter(item =>
            !item.key || isMenuItemEnabled(item.key)
        ),
        [allSidebarItems, isMenuItemEnabled]
    );

    // Footer items for sidebar
    const footerItems: NavItemType[] = [
        {
            label: t('support'),
            href: '#',
            icon: HelpCircle
        },
    ];

    const env = import.meta.env.VITE_ENV;
    const hasEnvironmentBanner = env !== "production";

    // Nascondi "Summon Eve" quando si è sulla pagina Ask Eve
    const isOnAskEvePage = currentPath === '/freight-forwarder/ask-eve';

    return (
        <div className={`flex h-screen bg-secondary ${hasEnvironmentBanner ? "pt-7" : ""}`}>
            {/* Sidebar Navigation */}
            <SidebarNavigationSimple
                activeUrl={currentPath}
                items={sidebarItems}
                footerItems={footerItems}
                featureCard={!isOnAskEvePage ? <AICTACard /> : undefined}
                showAccountCard={true}
                className="hidden lg:flex"
            />

            {/* Page Content */}
            <main className="flex-1 overflow-auto bg-secondary">
                <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
                    <MenuRouteGuard>
                        <Outlet />
                    </MenuRouteGuard>
                </div>
            </main>
        </div>
    );
};

